import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../../club/ClubProvider.tsx'
import { unitSymbol, useProducts, useStockLocations, useStockMovements } from '../../data/catalog.ts'
import { useAreaVersions } from '../../data/geo.ts'
import { useJobAreas, useJobs, weekOf } from '../../data/plan.ts'
import { engagedByProduct, locationLabel, lowCount, stockRows, useTreatmentPlansLite, type StockRow } from '../../data/stockUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { localDate } from '../../lib/time.ts'
import { Field, Pill } from '../../ui/components.tsx'
import { RowLink } from '../../ui/RowLink.tsx'
import { useWriteAccess } from '../../ui/hooks.ts'
import { Chevron, useLang, useStockAccess } from './shared.tsx'

/** M15-A, Catalogue › Stock (docs/M15.md "Screens"): on hand per product, from the device's copy of the ledger (the twin's exact sums; the server's sum is the
 *  truth), a product under the club's own minimum first with a sand pill (only when a minimum is set: a warning, never an order), "engagé" from the verified
 *  treatment plans of the week (C's rows, when there are), the places under each row, a search, "Réceptionner" for the storeroom. Works offline. Placed in
 *  Catalogue.tsx by Z's close; "Inventaire" (M15-B's counts) placed by the close of release b, for whoever counts or opens a count. */
export function StockTab() {
  const { t } = useTranslation()
  const lang = useLang()
  const { site } = useClub()
  const access = useStockAccess()
  const counting = useWriteAccess('stock.record')
  const products = useProducts(); const movements = useStockMovements(); const locations = useStockLocations()
  const plans = useTreatmentPlansLite(); const jobs = useJobs(); const jobAreas = useJobAreas(); const versions = useAreaVersions()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const rows = useMemo(() => stockRows(products ?? [], movements ?? [], query), [products, movements, query])
  const engaged = useMemo(() => {
    if (!plans || !jobs || !jobAreas || !versions) return new Map()
    const today = localDate(new Date(), site.timezone)
    const areaM2 = new Map<string, number>()
    for (const v of versions) if (/,\s*\)$/.test(v.valid_during)) areaM2.set(v.area_id, Number(v.area_m2))
    const byJob = new Map<string, string[]>()
    for (const ja of jobAreas) if (!ja.removed_by_command_id) byJob.set(ja.job_id, [...(byJob.get(ja.job_id) ?? []), ja.area_id])
    return engagedByProduct(plans, new Map(jobs.filter((j) => j.state !== 'cancelled').map((j) => [j.id, j.scheduled_local_date])), byJob, areaM2, new Set(weekOf(today)))
  }, [plans, jobs, jobAreas, versions, site.timezone])
  const placeName = useMemo(() => new Map((locations ?? []).map((l) => [l.id, locationLabel(l, lang)])), [locations, lang])
  if (!products || !movements || !locations) return <p>{t('common.loading')}</p>
  const low = lowCount(rows)
  return (
    <section className="flex flex-col gap-4" data-testid="stock-panel">
      <p className="meta">{t('stock.help')}</p>
      {access.permitted && <RowLink to="/catalogue/receive" icon="box" tone="sand" testId="stock-receive" title={t('stock.receive')} hint={t('stock.receiveHint')} />}
      {(counting.permitted || access.permitted) && <RowLink to="/catalogue/count" icon="clipboard" tone="sand" testId="stock-count" title={t('count.entry.title')} hint={t('count.entry.hint')} />}
      <Field label={t('stock.search')} htmlFor="stock-search">
        <input id="stock-search" type="search" className="input" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('catalogue.searchPlaceholder')} data-testid="stock-search" />
      </Field>
      {low > 0 && <p className="font-semibold text-amber-900 dark:text-amber-200" data-testid="stock-low-count" data-count={low}>{t('stock.lowCount', { count: low })}</p>}
      {rows.length === 0 && <p className="opacity-70" data-testid="stock-none">{movements.length === 0 ? t('stock.empty') : t('stock.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="stock-list">
        {rows.map((r) => <StockRowCard key={r.product.id} r={r} open={open === r.product.id} onToggle={() => setOpen(open === r.product.id ? null : r.product.id)} lang={lang} placeName={placeName} engaged={engaged.get(r.product.id)} />)}
      </ul>
    </section>
  )
}

function StockRowCard({ r, open, onToggle, lang, placeName, engaged }: { r: StockRow; open: boolean; onToggle: () => void; lang: 'fr' | 'en'; placeName: ReadonlyMap<string, string>; engaged: { amount: string; unit: 'kg' | 'L' } | undefined }) {
  const { t } = useTranslation()
  const unit = unitSymbol(r.product.base_unit_code, lang)
  const tone = r.negative ? '!border-red-400 dark:!border-red-700' : r.low ? '!border-amber-400 dark:!border-amber-600' : ''
  return (
    <li className={`card flex flex-col gap-2 !border-2 ${tone}`} data-testid="stock-row" data-product={r.product.id} data-low={r.low ? '1' : '0'} data-negative={r.negative ? '1' : '0'}>
      {/* the whole row is the toggle, 56 px at least (M15a ergonomics review: a one-line name made a 34-px target) */}
      <button type="button" className="flex min-h-14 w-full items-center justify-between gap-3 py-1 text-left" aria-expanded={open} aria-controls={`stock-${r.product.id}`} onClick={onToggle} data-testid="stock-toggle">
        <span className="flex min-w-0 flex-col gap-1">
          <span className="min-w-0 break-words text-lg font-bold" data-testid="stock-name">{r.product.name}</span>
          <span className="flex flex-wrap items-center gap-1.5">
            {r.negative && <span data-testid="stock-negative-pill"><Pill tone="red">{t('stock.negative')}</Pill></span>}
            {r.low && <span data-testid="stock-low-pill"><Pill tone="amber">{t('stock.low', { min: fmtNum(r.min ?? 0, lang, 3), unit })}</Pill></span>}
            {r.product.archived_at && <Pill tone="gray">{t('catalogue.archived')}</Pill>}
            {engaged && <span className="meta" data-testid="stock-engaged">{t('stock.engaged', { amount: fmtNum(Number(engaged.amount), lang, 3), unit: unitSymbol(engaged.unit, lang) })}</span>}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="text-2xl font-bold tabular-nums" data-testid="stock-on-hand">{fmtNum(Number(r.onHand), lang, 3)} {unit}</span>
          <Chevron open={open} />
        </span>
      </button>
      {open && (
        <div id={`stock-${r.product.id}`} className="flex flex-col gap-2 border-t-2 border-slate-200 pt-2 dark:border-slate-700" data-testid="stock-detail">
          <p className="meta">{r.min !== null ? t('stock.min', { min: fmtNum(r.min, lang, 3), unit }) : t('stock.noMin')}</p>
          {r.byLocation.size > 0 && (
            <ul className="flex flex-col gap-1" data-testid="stock-places">
              {[...r.byLocation].map(([loc, qty]) => <li key={loc} className="flex justify-between gap-3" data-location={loc}><span>{placeName.get(loc) ?? '…'}</span><span className="font-semibold tabular-nums">{fmtNum(Number(qty), lang, 3)} {unit}</span></li>)}
            </ul>
          )}
          <Link to="/catalogue" search={{ p: r.product.id }} className="btn w-full" data-testid="stock-open-product">{t('stock.openProduct')}</Link>
        </div>
      )}
    </li>
  )
}
